import { useCallback, useState } from 'react';

import { AttachmentWithStatus, AttachmentsById } from '../../../../resources/discussion/types';

type AllAttachments = Record<string, AttachmentsById>;

/**
 * Custom hook to manage attachment state for messages being composed in the discussion.
 *
 * Handles attachment lifecycle including:
 * - Storing attachments in React state by sendingMessageId (supports multiple concurrent drafts)
 * - Adding/removing/retrieving attachments with unique filenameId keys
 * - Automatic cleanup when messages are successfully sent (sendingMessageId removed from list)
 *
 * Attachments remain visible during failed sends to allow retry with the same attachments.
 *
 * @param sendingMessageIds - Array of active sending message IDs (from Redux status, includes pending/error).
 * There is multiple sendingMessageIds to handle multiple concurrent sending message (if one message takes very long time to send)
 * @returns Object containing:
 *   - allAttachments: Complete attachment state indexed by sendingMessageId
 *   - getAttachments: Retrieve attachments for a specific sendingMessageId
 *   - addAttachment: Add or update an attachment
 *   - removeAttachment: Remove a single attachment by filenameId
 */
export function useAttachmentManagement(sendingMessageIds: string[]) {
    const [allAttachments, setAllAttachments] = useState<AllAttachments>({});
    const [prevSendingIds, setPrevSendingIds] = useState(sendingMessageIds);

    const getAttachmentsForMessage = useCallback(
        (sendingMessageId: string): AttachmentsById => allAttachments[sendingMessageId] || {},
        [allAttachments],
    );

    const addAttachment = useCallback(
        (sendingMessageId: string, filenameId: string, attachment: AttachmentWithStatus) => {
            setAllAttachments((prev) => ({
                ...prev,
                [sendingMessageId]: {
                    ...prev[sendingMessageId],
                    [filenameId]: attachment,
                },
            }));
        },
        [],
    );

    const removeAttachment = useCallback((sendingMessageId: string, filenameId: string) => {
        setAllAttachments((prev) => {
            const currentAttachments = { ...prev[sendingMessageId] };
            delete currentAttachments[filenameId];
            return { ...prev, [sendingMessageId]: currentAttachments };
        });
    }, []);

    // Remove attachments for messages that are no longer sending (derived state during render)
    // Compare by length (primitive) to avoid reference-equality false positives with inline arrays
    if (prevSendingIds.length !== sendingMessageIds.length) {
        setPrevSendingIds(sendingMessageIds);
        if (prevSendingIds.length > sendingMessageIds.length) {
            const removedIds = prevSendingIds.filter((id) => !sendingMessageIds.includes(id));
            if (removedIds.length > 0) {
                setAllAttachments((prev) => {
                    const next = { ...prev };
                    removedIds.forEach((id) => delete next[id]);
                    return next;
                });
            }
        }
    }

    return {
        allAttachments,
        getAttachments: getAttachmentsForMessage,
        addAttachment,
        removeAttachment,
    };
}
